import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useDeliveryOrders from '../hooks/useDeliveryOrders';
import DeliveryChallanActions from '../components/delivery-challan/DeliveryChallanActions';
import DeliveryChallanPrintedTable from '../components/delivery-challan/DeliveryChallanPrintedTable';
import { useErrorDialog } from '../context/errorDialogContext';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import Pagination from '../components/common/Pagination';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import DeliveryOrderViewModal from '../components/delivery-orders/DeliveryOrderViewModal';
import deliveryOrderService from '../services/deliveryOrderService';
import ChallanSuccessModal from '../components/delivery-orders/ChallanSuccessModal';
import ExportModal from '../components/common/ExportModal';
import { printTableData } from '../utils/tablePrintHelper';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'delivery_challan_printed_page_size';

const DeliveryChallanPrintedPage = () => {
    const { t } = useTranslation();
    const {
        orders,
        total,
        loading,
        error,
        fetchOrders,
        fetchOrderById,
        exportChallans,
        markChallanPrinted,
        markDelivered,
    } = useDeliveryOrders('/delivery-challans');

    const [searchTerm, setSearchTerm] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const today = new Date().toLocaleDateString('en-CA');
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });
    const searchDebounceRef = useRef(null);

    const [viewingOrder, setViewingOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    const [showChallanSuccessModal, setShowChallanSuccessModal] = useState(false);
    const [challanResultData, setChallanResultData] = useState(null);
    const [challanSubOrderId, setChallanSubOrderId] = useState(null);

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const { showError } = useErrorDialog();
    const { selectedWarehouseIds } = useWarehouseContext();
    const { userRole } = useRole();

    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchTerm]);

    useEffect(() => {
        const skip = (currentPage - 1) * pageSize;
        const params = { skip, limit: pageSize, challan_printed: true, gst_included: false };
        if (committedSearch) params.search = committedSearch;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        params.status = statusFilter ? [statusFilter] : ['in_transit', 'delivered'];
        fetchOrders(params);
    }, [fetchOrders, currentPage, committedSearch, statusFilter, fromDate, toDate, selectedWarehouseIds, pageSize]);

    useEffect(() => {
        if (error) showError(error, 'Error');
    }, [error, showError]);

    const filteredOrders = useMemo(() => {
        return [...(orders || [])]
            .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
    }, [orders]);

    const handleStatusChange = (val) => { setStatusFilter(val); setCurrentPage(1); };
    const handleFromDateChange = (val) => { setFromDate(val); setCurrentPage(1); };
    const handleToDateChange = (val) => { setToDate(val); setCurrentPage(1); };

    const handleRefresh = async () => {
        const skip = (currentPage - 1) * pageSize;
        const params = { skip, limit: pageSize, challan_printed: true, gst_included: false };
        if (committedSearch) params.search = committedSearch;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        params.status = statusFilter ? [statusFilter] : ['in_transit', 'delivered'];
        await fetchOrders(params);
    };

    const handlePrintPage = () => {
        const columns = [
            { id: 'challan_number', label: t('deliveryChallanModule.table.columns.challanNumber') },
            { id: 'sub_order_number', label: t('deliveryChallanModule.table.columns.subOrderNumber') },
            { id: 'order_number', label: t('deliveryChallanModule.table.columns.orderNumber') },
            { id: 'tally_name', label: t('deliveryChallanModule.table.columns.tallyName') },
            { id: 'location_name', label: t('deliveryChallanModule.table.columns.location') },
            { id: 'vehicle_number', label: t('commonCols.vehicleNumber') },
            { id: 'total_quantity', label: t('deliveryChallanModule.table.columns.totalQty') },
            { id: 'status', label: t('deliveryChallanModule.table.columns.status') },
        ];
        printTableData(filteredOrders, columns, 'Delivery Challan Printed');
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleExport = async ({ from_date, to_date, format }) => {
        setExportLoading(true);
        try {
            const result = await exportChallans({ from_date, to_date, format, challan_printed: true, gst_included: false });
            if (result.success) {
                const url = URL.createObjectURL(result.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `delivery-challans-printed-${from_date}-to-${to_date}.${format === 'excel' ? 'xlsx' : 'csv'}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setShowExportModal(false);
            } else {
                showError(result.error || 'Export failed', 'Error');
            }
        } finally {
            setExportLoading(false);
        }
    };

    const handleViewOrder = async (subOrder) => {
        if (subOrder.id) {
            setViewLoading(true);
            setViewingOrder(null);
            setShowViewModal(true);
            try {
                const result = await fetchOrderById(subOrder.challan_number || subOrder.id);
                if (result.success && result.data) setViewingOrder(result.data);
                else showError(result.error || 'Failed to fetch order details', 'Error');
            } catch (err) {
                showError(err.message || 'Failed to fetch order details', 'Error');
            } finally {
                setViewLoading(false);
            }
        }
    };

    const handlePrintChallan = async (order) => {
        const result = await fetchOrderById(order.challan_number || order.id);
        if (result.success) {
            setChallanResultData(result.data || null);
            setChallanSubOrderId(order.id);
            setShowChallanSuccessModal(true);
        } else {
            showError(result.error || 'Failed to fetch order details', 'Error');
        }
    };

    const realTotal = useMemo(() => {
        if (currentPage === 1 && filteredOrders.length < pageSize && total > filteredOrders.length) {
            return filteredOrders.length;
        }
        return total;
    }, [total, filteredOrders.length, currentPage, pageSize]);

    const totalPages = Math.ceil(realTotal / pageSize);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        Delivery Challan Printed
                    </h1>
                    <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                        Delivery challans that have been printed
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <div
                        className="flex items-center gap-3 rounded-2xl border px-4 py-3"
                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                    >
                        <div
                            className="flex h-12 w-12 items-center justify-center rounded-full"
                            style={{
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.15),
                                color: UI_COLORS.STATUS.SUCCESS,
                            }}
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                Total Printed
                            </p>
                            <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {realTotal}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <DeliveryChallanActions
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                onRefresh={handleRefresh}
                refreshLoading={loading}
                onPrint={handlePrintPage}
                statusValue={statusFilter}
                onStatusChange={handleStatusChange}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
                onExport={() => setShowExportModal(true)}
                exportLoading={exportLoading}
                canExport={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.OPERATIONS_MANAGER].includes(userRole)}
            />

            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
                <DeliveryChallanPrintedTable
                    orders={filteredOrders}
                    loading={loading}
                    onViewOrder={handleViewOrder}
                    onPrintChallan={handlePrintChallan}
                    onMarkDelivered={markDelivered}
                    onRefresh={handleRefresh}
                    userRole={userRole}
                />
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={realTotal}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total printed challans"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {showViewModal && (
                <DeliveryOrderViewModal
                    isOpen={showViewModal}
                    onClose={() => { setShowViewModal(false); setViewingOrder(null); }}
                    order={viewingOrder}
                    loading={viewLoading}
                />
            )}

            {showExportModal && (
                <ExportModal
                    isOpen={showExportModal}
                    onClose={() => setShowExportModal(false)}
                    onExport={handleExport}
                    loading={exportLoading}
                    title="Export Printed Delivery Challans"
                />
            )}

            {showChallanSuccessModal && (
                <ChallanSuccessModal
                    isOpen={showChallanSuccessModal}
                    data={challanResultData}
                    onClose={() => { setShowChallanSuccessModal(false); setChallanResultData(null); setChallanSubOrderId(null); }}
                    onSendNotification={challanSubOrderId ? (type) => deliveryOrderService.sendChallanNotification(challanSubOrderId, type) : undefined}
                    onMarkChallanPrinted={challanSubOrderId ? () => markChallanPrinted(challanSubOrderId) : undefined}
                    userRole={userRole}
                    onEditSuccess={() => { fetchOrders(); }}
                />
            )}
        </div>
    );
};

export default DeliveryChallanPrintedPage;
